import { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getTestPeople, setTestUnlock, giveExtraTry, getTestResult } from '../utils/api';
import PersonalityReport, { profileSummary, styleName, styleColor } from './PersonalityReport';
import { ExamResults, TEST_CSS } from '../pages/TakeTest';

/* University Admin → Tests & Unlocks.
   Pick a test, then unlock it for everyone or person by person. A rep sees it within seconds.
   Field Rep Exam: 2 tries per unlock; the rep sees their score. Closer Profile: 1 try; only
   admins ever see the result (View report).                                               */

const CSS = `
.ta-tests{display:grid;grid-template-columns:1fr 1fr;gap:12px;max-width:900px;margin-bottom:16px}
.ta-test{text-align:left;font:inherit;cursor:pointer;background:#fff;border:2px solid #DCE6E1;border-radius:14px;padding:14px 16px}
.ta-test.on{border-color:#0D3B2E;box-shadow:0 8px 20px -12px rgba(13,59,46,.55)}
.ta-test b{display:block;font-size:16px;color:#12281F}
.ta-test span{font-size:12.5px;color:#6b7a73}
.ta-test .nums{margin-top:6px;font-size:12.5px;font-weight:700;color:#0D3B2E}
.ta-bar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:12px;max-width:900px}
.ta-bar input{flex:1;min-width:180px;max-width:280px;padding:9px 12px;border:1px solid #d6dfdb;border-radius:9px;font:inherit}
.ta-b{font:inherit;font-weight:700;font-size:13px;border-radius:9px;padding:8px 14px;cursor:pointer;border:1.5px solid #0D3B2E;background:#0D3B2E;color:#fff;white-space:nowrap}
.ta-b.alt{background:#fff;color:#0D3B2E}
.ta-b.lock{background:#fff;color:#8a3b14;border-color:#d9a27e}
.ta-b.sm{padding:5px 11px;font-size:12px}
.ta-b:disabled{opacity:.5;cursor:default}
.ta-list{background:#fff;border:1px solid #E3E9E5;border-radius:14px;overflow:hidden;max-width:900px}
.ta-row{display:grid;grid-template-columns:minmax(150px,1.3fr) 150px minmax(170px,1.4fr) 130px;gap:12px;align-items:center;padding:11px 16px;border-top:1px solid #EEF2F0}
.ta-row:first-child{border-top:0}
.ta-row.head{background:#F8FAF9;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#7a8a82}
.ta-row.off{opacity:.55}
.ta-nm b{display:block;color:#12281F}
.ta-nm span{font-size:12px;color:#7a8a82}
.ta-st{display:inline-block;font-size:11.5px;font-weight:800;padding:3px 9px;border-radius:99px}
.ta-st.locked{background:#ECEFF1;color:#546E7A}.ta-st.open{background:#FFF3D6;color:#8A5A00}.ta-st.done{background:#e2f2e9;color:#1f7a4a}
.ta-res{font-size:13px;display:flex;flex-wrap:wrap;gap:6px;align-items:center}
.ta-chip{font:inherit;font-size:12px;font-weight:700;border-radius:7px;padding:3px 9px;border:1px solid #d6dfdb;background:#fff;cursor:pointer}
.ta-chip.pass{border-color:#9fd3b5;color:#1f7a4a}.ta-chip.fail{border-color:#efb3ac;color:#b3362c}
.ta-acts{display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap}
.ta-note{font-size:12.5px;color:#7a8a82;max-width:900px;margin-top:10px}
.ta-modal{position:fixed;inset:0;z-index:9999;background:rgba(12,28,22,.55);display:flex;align-items:flex-start;justify-content:center;padding:24px 14px;overflow-y:auto}
.ta-sheet{background:#eef0f2;border-radius:16px;max-width:760px;width:100%;padding:20px;position:relative}
.ta-x{position:absolute;top:12px;right:12px;width:36px;height:36px;border-radius:50%;border:0;background:#fff;font-size:16px;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.15)}
@media(max-width:720px){.ta-tests{grid-template-columns:1fr}.ta-row{grid-template-columns:1fr auto}.ta-row.head{display:none}.ta-res{grid-column:1/-1}.ta-acts{grid-column:1/-1;justify-content:flex-start}}
`;

const fmtDate = s => { if (!s) return ''; const d = new Date(String(s).replace(' ', 'T') + 'Z'); return isNaN(d) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

export default function TestsAdmin() {
  const [data, setData] = useState(null);
  const [key, setKey] = useState('field-rep-exam');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);       // id being changed, or 'all'
  const [view, setView] = useState(null);       // { kind, name, role, data }

  const load = useCallback(() => getTestPeople().then(setData).catch(() => toast.error('Could not load the tests')), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const test = data?.tests.find(t => t.key === key);
  const people = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.people || []).filter(p => !s || `${p.name} ${p.username} ${p.staffRole}`.toLowerCase().includes(s))
      .sort((a, b) => (b.active - a.active) || a.name.localeCompare(b.name));
  }, [data, q]);
  const counts = k => {
    const ps = (data?.people || []).filter(p => p.active);
    return { open: ps.filter(p => p.tests[k].unlocked).length, done: ps.filter(p => p.tests[k].used > 0).length, all: ps.length };
  };

  async function unlock(ids, unlocked, tag) {
    setBusy(tag);
    try { await setTestUnlock(key, { user_ids: ids, unlocked }); await load(); toast.success(`${unlocked ? 'Unlocked' : 'Locked'}${ids.length > 1 ? ` for ${ids.length} people` : ''}`); }
    catch { toast.error('Could not change that'); }
    finally { setBusy(null); }
  }
  async function extra(id) {
    setBusy(id);
    try { await giveExtraTry(key, id); await load(); toast.success('One more try given'); }
    catch { toast.error('Could not change that'); }
    finally { setBusy(null); }
  }
  async function open(p, attempt) {
    try {
      const r = await getTestResult(attempt.id);
      setView({ kind: r.key === 'closer-profile' ? 'profile' : 'exam', name: p.name, role: p.staffRole, data: r.result, at: r.at });
    } catch { toast.error('Could not open that result'); }
  }

  if (!data) return <div style={{ padding: 20, color: '#aaa' }}>Loading tests…</div>;
  const activeIds = (data.people || []).filter(p => p.active).map(p => p.id);

  return (
    <div>
      <style>{CSS + TEST_CSS}</style>
      <div className="ta-tests">
        {data.tests.map(t => {
          const c = counts(t.key);
          return (
            <button key={t.key} className={'ta-test' + (key === t.key ? ' on' : '')} onClick={() => setKey(t.key)}>
              <b>{t.kind === 'exam' ? '📝' : '🧭'} {t.title}</b>
              <span>{t.blurb} · {t.tries} {t.tries === 1 ? 'try' : 'tries'}{t.kind === 'profile' ? ' · results visible to admins only' : ''}</span>
              <div className="nums">{c.open} of {c.all} unlocked · {c.done} taken</div>
            </button>
          );
        })}
      </div>

      <div className="ta-bar">
        <input placeholder="Search name or role" value={q} onChange={e => setQ(e.target.value)} />
        <button className="ta-b" disabled={busy === 'all'} onClick={() => unlock(activeIds, true, 'all')}>🔓 Unlock for all</button>
        <button className="ta-b lock" disabled={busy === 'all'} onClick={() => unlock(activeIds, false, 'all')}>🔒 Lock for all</button>
      </div>

      <div className="ta-list">
        <div className="ta-row head"><div>Person</div><div>Status</div><div>Result</div><div style={{ textAlign: 'right' }}>Access</div></div>
        {people.length === 0 && <div style={{ padding: 18, color: '#7a8a82' }}>No staff logins found. Add logins for reps under Users.</div>}
        {people.map(p => {
          const s = p.tests[key];
          const status = s.used > 0 && !s.canTake ? ['done', test.kind === 'profile' ? 'Submitted' : 'Done'] : s.unlocked ? ['open', s.used ? `Unlocked · ${s.left} ${s.left === 1 ? 'try' : 'tries'} left` : 'Unlocked · not started'] : ['locked', 'Locked'];
          return (
            <div key={p.id} className={'ta-row' + (p.active ? '' : ' off')}>
              <div className="ta-nm"><b>{p.name}</b><span>{p.staffRole}{p.active ? '' : ' · inactive'}</span></div>
              <div><span className={'ta-st ' + status[0]}>{status[1]}</span></div>
              <div className="ta-res">
                {s.tries.length === 0 && <span style={{ color: '#9aa3a0' }}>—</span>}
                {test.kind === 'exam' && s.tries.map((a, i) => (
                  <button key={a.id} className={'ta-chip ' + (a.passed ? 'pass' : 'fail')} title={`Try ${i + 1} · ${fmtDate(a.at)} · click to see answers`} onClick={() => open(p, a)}>
                    {a.score}/{a.total} {a.passed ? '✓' : '✗'}
                  </button>
                ))}
                {test.kind === 'profile' && s.tries.map(a => (
                  <button key={a.id} className="ta-chip" onClick={() => open(p, a)} title={fmtDate(a.at)}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: styleColor(a.style), marginRight: 5 }} />{styleName(a.style)} · View report
                  </button>
                ))}
              </div>
              <div className="ta-acts">
                {s.unlocked
                  ? <button className="ta-b lock sm" disabled={busy === p.id} onClick={() => unlock([p.id], false, p.id)}>Lock</button>
                  : <button className="ta-b sm" disabled={busy === p.id} onClick={() => unlock([p.id], true, p.id)}>Unlock</button>}
                {s.used > 0 && s.left === 0 && <button className="ta-b alt sm" disabled={busy === p.id} onClick={() => extra(p.id)} title="Let them take it one more time">+1 try</button>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="ta-note">
        Reps only see a test after you unlock it for them, and it appears on their University page within a few seconds. Locking it again stops new tries; results are kept.
        {test.kind === 'profile' ? ' The person never sees their profile, only "Thank you, submitted". Inactive staff are not included in Unlock for all.' : ' Each unlock gives 2 tries. Use +1 try if someone needs another.'}
      </div>

      {view && (
        <div className="ta-modal" onClick={e => { if (e.target === e.currentTarget) setView(null); }}>
          <div className="ta-sheet">
            <button className="ta-x" onClick={() => setView(null)} aria-label="Close">✕</button>
            {view.kind === 'profile' ? (
              <>
                <PersonalityReport name={view.name} role={view.role} t={view.data.t} m={view.data.m} />
                <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  <button className="ta-b" onClick={() => navigator.clipboard.writeText(profileSummary(view.name, view.data.t, view.data.m)).then(() => toast.success('Summary copied')).catch(() => toast.error('Could not copy'))}>Copy summary</button>
                </div>
              </>
            ) : (
              <div className="tt" style={{ background: 'transparent' }}>
                <div style={{ fontSize: 13, color: '#5d6876', fontWeight: 700 }}>{view.name} · {fmtDate(view.at)}</div>
                <ExamResults r={view.data} title="CPR Field Rep Exam" onBack={() => setView(null)} left={null} backLabel="Close" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
